import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  PortalBuilderFlatTeamSettingGroupById,
  type PortalBuilderFlatTeamSettingGroupId,
} from "./groups";

type PortalBuilderSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: PortalBuilderSettingInput,
): PortalBuilderSettingInput {
  return metadata;
}

function portalBuilderGroup<
  const Group extends PortalBuilderFlatTeamSettingGroupId,
  const T extends Record<string, PortalBuilderSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const PortalBuilderRegistry = {
  ...portalBuilderGroup("portal_builder.overview", {
    "returns/disableRejectedItems": setting({
      label: "Pre-disable rejected items",
      description:
        "Show the rejection message in a tooltip on the select-items page instead of opening the return flow modal for items that can't be returned.",
      synonyms: [
        "pre-disable rejected items",
        "disable rejected items",
        "reject items tooltip",
      ],
    }),
    bulkReturnsEnabled: setting({
      label: "Enable bulk returns",
      description:
        "Allow shoppers to return the full quantity of a product at once using the same return reason for each item.",
      synonyms: ["enable bulk returns", "bulk returns", "return all quantity"],
    }),
    "returns/bulkReturns/collections": setting({
      label: "Collections for bulk returns",
      description:
        "Products in these collections can be bulk returned by customers. If empty, all products can be bulk returned.",
      synonyms: [
        "bulk return collections",
        "collections for bulk returns",
        "bulk returnable collections",
      ],
    }),
    "returns/bulkReturns/hideQuantity": setting({
      label: "Hide quantity controls for bulk returnable products",
      description:
        "Prevent customers from selecting specific quantities for bulk-returnable products, hiding the quantity controls and combining the price for all items.",
      synonyms: [
        "hide quantity controls",
        "bulk returns hide quantity",
        "hide quantity bulk return",
      ],
    }),
    "returns/portalCollectionBadges": setting({
      label: "Collection messages",
      description:
        'Show a short message, such as "Exchange or store credit only", on each item in the portal\'s select-items page whose product is in the chosen collections. The message is display copy only.',
      synonyms: [
        "collection messages",
        "collection badge",
        "badge by collection",
        "exchange only message",
        "store credit only message",
        "item eligibility message",
        "line item message",
      ],
    }),
    "warranties/showOtherOption": setting({
      label: "Show 'Other' option",
      description:
        "Show an 'Other' option as a selectable purchase channel on the warranty registration purchase-channel page.",
      synonyms: [
        "show other option",
        "other purchase channel",
        "other retailer option",
      ],
    }),
    "warranties/mailingListSignUpEnabled": setting({
      label: "Enable mailing list sign-up",
      description:
        "Show a mailing-list opt-in checkbox on the warranty registration contact-information screen.",
      synonyms: [
        "mailing list sign up",
        "mailing list sign-up",
        "mailing list signup",
        "newsletter signup",
        "enable mailing list sign-up",
      ],
    }),
    "warranties/cantFindItemButtonEnabled": setting({
      label: "Enable 'Can't find your item?' button",
      description:
        "Show a 'Can't find your item?' button on the warranty registration catalog screen.",
      synonyms: [
        "cant find item button",
        "can't find your item",
        "missing item button",
        "enable can't find your item button",
        "cant find your item button",
      ],
    }),
    "warranties/askShopperForPricePaid": setting({
      label: "Ask shopper for price paid",
      description:
        "Ask the shopper for the price they paid when registering an item for warranty.",
      synonyms: ["ask for price paid", "price paid", "shopper price paid"],
    }),
    "warranties/allowQuantitySelect": setting({
      label: "Allow quantity select",
      description:
        "Let shoppers select a quantity when registering an item for warranty.",
      synonyms: [
        "allow quantity select",
        "warranty quantity select",
        "select quantity registration",
      ],
    }),
    "warranties/maxQuantitySelect": setting({
      label: "Max quantity",
      description:
        "Maximum quantity a shopper may select when registering an item (blank means no limit).",
      synonyms: [
        "max quantity",
        "maximum quantity select",
        "warranty max quantity",
      ],
    }),
    portalExcludedProductTags: setting({
      label: "Hidden product tags",
      description:
        "Products with these tags are completely hidden from customers in the portal and shown as non-returnable for merchant-created returns.",
      synonyms: ["hidden product tags", "portal excluded tags"],
    }),
    portalExcludedProductIds: setting({
      label: "Hidden product IDs",
      description:
        "Products with these IDs are completely hidden from customers in the portal and shown as non-returnable for merchant-created returns.",
      synonyms: [
        "hidden product ids",
        "excluded product ids",
        "portal excluded ids",
      ],
    }),
    portalExcludedCollections: setting({
      label: "Hidden collections",
      description:
        "Products in these collections are completely hidden from customers in the portal and shown as non-returnable for merchant-created returns.",
      synonyms: ["hidden collections", "portal excluded collections"],
    }),
    "resourceOverride/COMMON": setting({
      label: "Portal text overrides",
      description:
        "Common resource-string overrides for portal copy such as the pickup modal text and the warranty link text.",
      synonyms: [
        "resource overrides",
        "portal text overrides",
        "portal copy overrides",
      ],
    }),
  }),
};

type PortalBuilderFlatTeamSettingKey = keyof typeof PortalBuilderRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const PortalBuilderFlatTeamSettingKeys = Object.keys(
  PortalBuilderRegistry,
) as PortalBuilderFlatTeamSettingKey[];

export const PortalBuilderFlatTeamSettingCatalogDefinitions =
  PortalBuilderFlatTeamSettingKeys.map((key) => {
    const definition = PortalBuilderRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: PortalBuilderFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    PortalBuilderFlatTeamSettingKey,
    PortalBuilderFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<PortalBuilderFlatTeamSettingKey>
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const PortalBuilderFlatTeamSettingMetadataByKey = Object.fromEntries(
  PortalBuilderFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<PortalBuilderFlatTeamSettingKey, SettingMetadata>;
